import type { LocaleCode } from '@/i18n/locales'
import { words } from './normalize'

/**
 * Spoken numbers to integers, for English and Hausa.
 *
 * This exists because "I'm twenty five" and "shekaru ashirin da biyar" are how
 * people actually answer an age question out loud, while a digits-only parser
 * would hear nothing and make the assistant ask again. Making the assistant
 * repeat itself is the fastest way to make a voice product feel broken.
 */

const EN_UNITS: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
}

const EN_TENS: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fourty: 40, // common mis-transcription
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
}

/**
 * Hausa numerals. Compounds join with "da": ashirin da biyar is 25.
 * Both the hooked and the unhooked spellings are accepted because recognisers
 * and keyboards disagree about ɗ, and a user should not lose an answer to an
 * orthography argument.
 */
const HA_UNITS: Record<string, number> = {
  sifili: 0,
  daya: 1,
  ɗaya: 1,
  biyu: 2,
  uku: 3,
  hudu: 4,
  huɗu: 4,
  biyar: 5,
  shida: 6,
  bakwai: 7,
  takwas: 8,
  tara: 9,
  goma: 10,
  sha: 10, // "sha biyar" = 15, handled as a teen prefix below
}

const HA_TENS: Record<string, number> = {
  ashirin: 20,
  talatin: 30,
  "arba'in": 40,
  hamsin: 50,
  sittin: 60,
  "saba'in": 70,
  tamanin: 80,
  "casa'in": 90,
}

const HA_HUNDRED = new Set(['dari', 'ɗari'])

function parseEnglish(tokens: readonly string[]): number | null {
  let total: number | null = null
  let pending = 0
  let sawWord = false

  for (const token of tokens) {
    if (token === 'and' || token === 'hundred') {
      if (token === 'hundred' && sawWord) pending *= 100
      continue
    }
    const unit = EN_UNITS[token]
    const ten = EN_TENS[token]
    if (unit !== undefined) {
      pending += unit
      sawWord = true
    } else if (ten !== undefined) {
      pending += ten
      sawWord = true
    } else if (sawWord) {
      break
    }
  }

  if (sawWord) total = pending
  return total
}

function parseHausa(tokens: readonly string[]): number | null {
  let pending = 0
  let sawWord = false
  let teenPrefix = false

  for (const token of tokens) {
    if (token === 'da') continue

    if (HA_HUNDRED.has(token)) {
      pending = pending === 0 ? 100 : pending * 100
      sawWord = true
      continue
    }

    const ten = HA_TENS[token]
    if (ten !== undefined) {
      pending += ten
      sawWord = true
      continue
    }

    const unit = HA_UNITS[token]
    if (unit !== undefined) {
      // "goma sha biyar" = 15: the sha marks a teen, so the following unit adds
      // to ten rather than standing alone.
      if (token === 'sha') {
        teenPrefix = true
        if (pending === 0) pending = 10
        sawWord = true
        continue
      }
      pending += unit
      sawWord = true
      if (teenPrefix) teenPrefix = false
      continue
    }

    if (sawWord) break
  }

  return sawWord ? pending : null
}

/**
 * Finds a number in spoken text. Digits win over words, because a recogniser
 * that produced digits was confident about them.
 */
export function parseSpokenNumber(input: string, locale: LocaleCode): number | null {
  const tokens = words(input)
  if (tokens.length === 0) return null

  for (const token of tokens) {
    const digits = token.match(/^\d{1,3}$/)
    if (digits) return Number(digits[0])
  }

  return locale === 'ha' ? parseHausa(tokens) : parseEnglish(tokens)
}
